import React, { useState } from "react";
import axios from "axios";
import {
  Phone,
  Droplets,
  Shield,
  Leaf,
  Zap,
  Star,
  Recycle,
  Wind,
  MapPin,
  Mail,
  Clock,
  ChevronDown,
  ChevronUp,
  Facebook,
  Instagram,
} from "lucide-react";

/* ─────────────────────────── DATA ─────────────────────────── */

const SERVICES = [
  {
    id: "culle",
    icon: Shield,
    eyebrow: "Servizio professionale",
    title: "Sanificazione di culle e accessori per bambini",
    desc: "La sicurezza dei più piccoli parte dalla pulizia. Offriamo un servizio professionale di sanificazione di culle, passeggini, seggiolini e accessori per neonati, eliminando batteri, acari, allergeni e cattivi odori in modo delicato ma efficace.\n\nUtilizziamo solo prodotti atossici e certificati, ideali per garantire un ambiente sano e protetto ai bambini. Il nostro trattamento senz'acqua assicura risultati impeccabili senza residui chimici, rispettando materiali e colori.",
    img: "https://lirp.cdn-website.com/7c20bad1/dms3rep/multi/opt/GettyImages-1187330045-1017w.jpg",
    imgLeft: false,
  },
  {
    id: "divani",
    icon: Droplets,
    eyebrow: "Un ambiente igienico e pulito",
    title: "Pulizia divani e materassi",
    desc: "Con il nostro sistema brevettato senz'acqua, eliminiamo macchie, polvere, acari e allergeni da divani, poltrone e materassi senza doverli spostare o bagnare. Il risultato è un ambiente più igienico, profumato e confortevole, pronto all'uso in pochi minuti.\n\nInterveniamo a Terni e provincia con attrezzature professionali e prodotti ecologici che proteggono i tessuti e ne prolungano la durata.",
    img: "https://lirp.cdn-website.com/7c20bad1/dms3rep/multi/opt/imgi_5_pulizia-profonda-e-professionale-del-materasso-1017w.webp",
    imgLeft: true,
  },
  {
    id: "tendaggi",
    icon: Zap,
    eyebrow: "Rimuove lo sporco in profondità",
    title: "Pulizia di tendaggi e tappeti in casa",
    desc: "I tendaggi e i tappeti sono veri e propri filtri dell'aria domestica, capaci di trattenere polvere, pollini e odori. Il nostro servizio di pulizia consente di rigenerarli senza doverli smontare o trasportare.\n\nRimuoviamo in profondità lo sporco e gli allergeni, restituendo freschezza e brillantezza ai colori, con un trattamento rapido, sicuro e totalmente ecosostenibile.",
    img: "https://lirp.cdn-website.com/7c20bad1/dms3rep/multi/opt/GettyImages-1273917543-1017w.jpg",
    imgLeft: false,
  },
  {
    id: "pelli",
    icon: Star,
    eyebrow: "Pulizia professionale",
    title: "Pulizia di pelli e alcantara",
    desc: "Trattiamo con la massima cura materiali pregiati come pelle, ecopelle e alcantara, utilizzando prodotti specifici che detergono, nutrono e proteggono le superfici.\n\nIl nostro metodo elimina macchie e impurità senza alterare la texture o il colore, restituendo morbidezza e luminosità. È la soluzione ideale per divani, poltrone, sedili auto e complementi d'arredo delicati.",
    img: "https://lirp.cdn-website.com/7c20bad1/dms3rep/multi/opt/GettyImages-1476900510-1017w.jpg",
    imgLeft: true,
  },
  {
    id: "animali",
    icon: Leaf,
    eyebrow: "Pet Friendly",
    title: "Pulizia di accessori per cani e gatti",
    desc: "Anche i nostri amici a quattro zampe meritano igiene e comfort! Offriamo un servizio dedicato alla sanificazione di cucce, trasportini, cuscini e accessori per animali domestici, eliminando germi, peli e odori in modo sicuro e naturale.\n\nTutti i prodotti utilizzati sono anallergici e pet-friendly, pensati per il benessere dell'animale e la serenità della famiglia.",
    img: "https://lirp.cdn-website.com/7c20bad1/dms3rep/multi/opt/GettyImages-1208322430-1017w.jpg",
    imgLeft: false,
  },
  {
    id: "restauro",
    icon: Star,
    eyebrow: "Tecniche professionali",
    title: "Restauro e rigenerazione pelli",
    desc: "Il nostro servizio di restauro pelli ridona nuova vita a divani, sedili e articoli in pelle usurata dal tempo. Interveniamo su graffi, screpolature e scolorimenti con trattamenti rigeneranti che ne ripristinano elasticità e lucentezza.\n\nGrazie a tecniche professionali e prodotti di alta qualità, la pelle torna a essere morbida, liscia e resistente, prolungandone la durata e il valore nel tempo.",
    img: "https://lirp.cdn-website.com/7c20bad1/dms3rep/multi/opt/GettyImages-898281526-1017w.jpg",
    imgLeft: true,
  },
];

const BENEFITS = [
  {
    title: "Pulizia rapida e senz'acqua",
    desc: "Permette di igienizzare a fondo ogni superficie senza bagnare i tessuti, eliminando macchie, acari e cattivi odori in tempi record e senza disagi per il cliente.",
  },
  {
    title: "Tecnologia innovativa e brevettata",
    desc: "Un sistema esclusivo che assicura risultati impeccabili, tessuti protetti e un'igiene profonda che dura nel tempo.",
  },
  {
    title: "Ecosostenibile e antiallergico",
    desc: "Ideale per famiglie, bambini e animali domestici, grazie all'utilizzo di prodotti biodegradabili e sicuri che rispettano la salute e l'ambiente.",
  },
];

const ECO_POINTS = [
  {
    icon: Droplets,
    title: "Zero consumo d'acqua",
    desc: "Il nostro sistema riduce fino al 100% l'utilizzo di acqua rispetto ai metodi tradizionali, contribuendo attivamente al risparmio idrico.",
  },
  {
    icon: Leaf,
    title: "Prodotti biodegradabili",
    desc: "Utilizziamo esclusivamente prodotti certificati, biodegradabili e sicuri per l'ambiente, là dove altri usano solventi inquinanti.",
  },
  {
    icon: Recycle,
    title: "Ciclo chiuso di rigenerazione",
    desc: "Il liquido pulente viene filtrato, analizzato e reintegrato automaticamente tramite capsule intelligenti brevettate, riducendo gli sprechi all'80%.",
  },
  {
    icon: Wind,
    title: "Zero scarichi nell'ambiente",
    desc: "Nessuno scarico di acque reflue, nessuna contaminazione del suolo. Il nostro processo è completamente a impatto ambientale quasi nullo.",
  },
];

const FAQ = [
  {
    q: "Dove si trova WashSofà?",
    a: "WashSofà si trova a Rieti – Via Curio Dentato 9 (area commerciale Aliante), 02100 Rieti (RI).",
  },
  {
    q: "Quali servizi offre WashSofà?",
    a: "WashSofà offre: eliminazione di cattivi odori, lavaggio moquette, lavaggio tappeti, lavaggio divani a domicilio, pulitura poltrone, lavaggio tende, pulizia pelli e alcantara, restauro pelli.",
  },
  {
    q: "È possibile ricevere il servizio a domicilio?",
    a: "Sì, WashSofà effettua servizi direttamente a domicilio a Rieti e provincia. Contattaci per fissare un appuntamento.",
  },
  {
    q: "Come posso contattare WashSofà?",
    a: "Puoi contattarci tramite telefono: +39 3463785564, email: washsofa1@gmail.com, o tramite il modulo preventivo presente su questa pagina. Siamo disponibili anche su WhatsApp.",
  },
  {
    q: "Quanto tempo richiede un intervento?",
    a: "I tempi variano in base al tipo di servizio e alla dimensione degli articoli. In generale, un divano medio viene trattato in 60-90 minuti. L'asciugatura è immediata grazie al metodo senz'acqua.",
  },
];

/* ─────────────────────────── COMPONENT ─────────────────────── */

export default function Home() {
  const [openFaq, setOpenFaq] = useState(null);
  const [form, setForm] = useState({
    nome: "",
    email: "",
    telefono: "",
    messaggio: "",
    prodotto: "",
    privacy: false,
    promozioni: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.email || !form.telefono) {
      setFormError(true);
      return;
    }
    setFormError(false);
    setSendError(false);
    setSending(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/send-email`, {
        nome: form.nome,
        email: form.email,
        telefono: form.telefono,
        prodotto: form.prodotto,
        messaggio: form.messaggio,
      });
      if (res.data.success) {
        setSubmitted(true);
      } else {
        setSendError(true);
      }
    } catch (err) {
      console.error("Errore invio:", err);
      setSendError(true);
    }
    setSending(false);
  };

  return (
    <div style={{ paddingTop: "72px" }}>

      {/* ═══════════════ HERO ═══════════════ */}
      <section id="hero" className="hero-section">
        <div className="hero-content fade-up">
          <h1
            style={{
              fontSize: "clamp(2rem, 5vw, 3.2rem)",
              fontWeight: 800,
              lineHeight: 1.25,
              marginBottom: "1.2rem",
              textShadow: "0 2px 12px rgba(0,0,0,0.3)",
            }}
          >
            Zero Acqua, Massima Pulizia:
            <br />
            il nuovo standard di igiene per la tua casa a Rieti
          </h1>
          <p
            style={{
              fontSize: "1.15rem",
              fontWeight: 400,
              marginBottom: "2.5rem",
              opacity: 0.92,
              textShadow: "0 1px 6px rgba(0,0,0,0.2)",
            }}
          >
            Con sistema brevettato senz'acqua, ecologico e sicuro per tutta la famiglia.
          </p>
          <a
            href="#servizi"
            onClick={(e) => {
              e.preventDefault();
              const el = document.getElementById("servizi");
              if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 72, behavior: "smooth" });
            }}
            style={{
              display: "inline-block",
              border: "2px solid rgba(255,255,255,0.8)",
              color: "white",
              padding: "0.75rem 2.2rem",
              borderRadius: "50px",
              fontWeight: 600,
              fontSize: "1rem",
              textDecoration: "none",
              backdropFilter: "blur(4px)",
              background: "rgba(255,255,255,0.1)",
              transition: "background 0.2s ease",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.25)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.1)"; }}
          >
            Scopri di più
          </a>
        </div>
      </section>

      {/* ═══════════════ CHI SIAMO ═══════════════ */}
      <section id="chi-siamo" style={{ padding: "5rem 2rem", background: "#fff" }}>
        <div style={{ maxWidth: "900px", margin: "0 auto", textAlign: "center" }}>
          <p className="section-eyebrow">WashSofà</p>
          <h2 style={{ fontSize: "2.2rem", fontWeight: 700, color: "#1B2C3E", marginBottom: "0.5rem" }}>
            Chi siamo
          </h2>
          <div className="section-divider-center"></div>
          <p className="prose-text">
            Siamo un'azienda specializzata nella cura, pulizia e sanificazione di tessuti e materiali di
            arredamento, dedicata a garantire ambienti domestici e professionali sani, igienizzati e
            confortevoli. La nostra missione è offrire servizi di alta qualità con un approccio sostenibile,
            attento sia al benessere delle persone che al rispetto dell'ambiente.
          </p>
          <p className="prose-text" style={{ marginTop: "1rem" }}>
            Operiamo direttamente a domicilio, con personale qualificato e attrezzature all'avanguardia,
            per assicurare risultati eccellenti in tempi rapidi e senza disagi per il cliente. Ogni intervento
            è eseguito con la massima attenzione ai dettagli, utilizzando prodotti specifici e metodologie
            testate per preservare la bellezza e la durata dei materiali trattati.
          </p>
        </div>
      </section>

      {/* ═══════════════ ZERO ACQUA ═══════════════ */}
      <section id="zero-acqua" style={{ background: "#f8fcfd", padding: "5rem 2rem" }}>
        <div
          style={{
            maxWidth: "1100px",
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "4rem",
            alignItems: "center",
          }}
          className="two-col-grid"
        >
          <div>
            <p className="section-eyebrow">Il metodo professionale per tutti i tuoi tessuti a Rieti</p>
            <h2 style={{ fontSize: "2rem", fontWeight: 700, color: "#1B2C3E", marginBottom: "0.5rem" }}>
              Zero Acqua, massima Pulizia
            </h2>
            <div className="section-divider"></div>
            <p className="prose-text">
              Il nostro metodo professionale rivoluziona il concetto di pulizia tradizionale: niente acqua,
              solo risultati impeccabili. Grazie a una tecnologia innovativa e brevettata, interveniamo su
              divani, materassi, tappeti e tendaggi direttamente a domicilio, garantendo una pulizia profonda,
              igienizzante e immediata, senza bagnare o rovinare i tessuti.
            </p>
            <p className="prose-text" style={{ marginTop: "1rem" }}>
              Operiamo a Rieti e provincia, offrendo un servizio rapido, ecologico e sicuro per tutta la
              famiglia. Il nostro sistema elimina efficacemente acari, allergeni, macchie e cattivi odori,
              restituendo freschezza e comfort agli ambienti in pochi minuti.
            </p>
          </div>
          <div className="img-hover" style={{ borderRadius: "12px", overflow: "hidden" }}>
            <img
              src="https://lirp.cdn-website.com/7c20bad1/dms3rep/multi/opt/imgi_3_pulizia-professionale-del-divano-due+%281%29-1017w.webp"
              alt="Pulizia zero acqua"
              style={{ width: "100%", height: "380px", objectFit: "cover", display: "block" }}
            />
          </div>
        </div>
      </section>

      {/* ═══════════════ SERVIZI ═══════════════ */}
      <section id="servizi" style={{ padding: "5rem 2rem", background: "#fff" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          {/* intro */}
          <div style={{ textAlign: "center", marginBottom: "1rem" }}>
            <p className="section-eyebrow">I nostri Servizi</p>
            <h2 style={{ fontSize: "2.2rem", fontWeight: 700, color: "#1B2C3E", marginBottom: "0.5rem" }}>
              Cosa facciamo
            </h2>
            <div className="section-divider-center"></div>
            <p className="prose-text" style={{ maxWidth: "700px", margin: "0 auto" }}>
              Offriamo un'ampia gamma di servizi dedicati alla pulizia, sanificazione e manutenzione di
              superfici e accessori. Interveniamo su divani, materassi, tappeti e tendaggi direttamente in
              casa, senza l'uso eccessivo di acqua e con tempi di asciugatura minimi.
            </p>
          </div>

          {/* Quick cards grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: "1.2rem",
              marginBottom: "4rem",
              marginTop: "2.5rem",
            }}
          >
            {SERVICES.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById(s.id);
                  if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 90, behavior: "smooth" });
                }}
                className="service-card"
                style={{ textDecoration: "none", cursor: "pointer" }}
              >
                <div
                  style={{
                    width: "52px",
                    height: "52px",
                    background: "rgba(23, 195, 206, 0.1)",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 1rem auto",
                  }}
                >
                  <s.icon size={22} color="#17C3CE" />
                </div>
                <h3 style={{ fontSize: "0.97rem", fontWeight: 600, color: "#1B2C3E" }}>{s.title}</h3>
              </a>
            ))}
          </div>

          {/* Detailed service sections */}
          {SERVICES.map((s, i) => (
            <div
              key={s.id}
              id={s.id}
              style={{
                paddingTop: "4rem",
                paddingBottom: "4rem",
                borderTop: "1px solid #eee",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "4rem",
                  alignItems: "center",
                }}
                className="service-grid"
              >
                <div
                  className="img-hover"
                  style={{
                    borderRadius: "12px",
                    overflow: "hidden",
                    order: s.imgLeft ? 0 : 1,
                  }}
                >
                  <img
                    src={s.img}
                    alt={s.title}
                    style={{ width: "100%", height: "340px", objectFit: "cover", display: "block" }}
                  />
                </div>
                <div style={{ order: s.imgLeft ? 1 : 0 }}>
                  <p className="section-eyebrow">{s.eyebrow}</p>
                  <h3 style={{ fontSize: "1.6rem", fontWeight: 700, color: "#1B2C3E", marginBottom: "0.5rem", lineHeight: 1.3 }}>
                    {s.title}
                  </h3>
                  <div className="section-divider"></div>
                  {s.desc.split("\n\n").map((para, pi) => (
                    <p key={pi} className="prose-text" style={{ marginBottom: "0.8rem" }}>{para}</p>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══════════════ PERCHÉ NOI ═══════════════ */}
      <section style={{ background: "#f8fcfd", padding: "5rem 2rem" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "3rem" }}>
            <h2 style={{ fontSize: "2.2rem", fontWeight: 700, color: "#1B2C3E" }}>
              Perché noi e non altri
            </h2>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "1.5rem",
            }}
          >
            {BENEFITS.map((b, i) => (
              <div
                key={i}
                style={{
                  background: "white",
                  borderRadius: "10px",
                  padding: "2rem",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                  borderTop: "4px solid #17C3CE",
                }}
              >
                <h3 style={{ fontSize: "1.05rem", fontWeight: 700, color: "#1B2C3E", marginBottom: "0.8rem" }}>
                  {b.title}
                </h3>
                <p style={{ color: "#666", lineHeight: 1.7, fontSize: "0.95rem" }}>{b.desc}</p>
              </div>
            ))}
          </div>
          <p style={{ textAlign: "center", fontWeight: 600, fontSize: "1.05rem", color: "#17C3CE", marginTop: "2.5rem" }}>
            Con noi, ogni pulizia diventa un trattamento professionale di benessere per la casa.
          </p>
        </div>
      </section>

      {/* ═══════════════ TECNOLOGIA ═══════════════ */}
      <section id="tecnologia" style={{ padding: "5rem 2rem", background: "#fff" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "3.5rem" }}>
            <p className="section-eyebrow">La tecnologia sostenibile per la pulizia professionale</p>
            <h2 style={{ fontSize: "2.2rem", fontWeight: 700, color: "#1B2C3E", marginBottom: "0.5rem" }}>
              My Personal Washer: zero acqua, zero compromessi
            </h2>
            <div className="section-divider-center"></div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "4rem",
              alignItems: "center",
              marginBottom: "4rem",
            }}
            className="two-col-grid"
          >
            <div>
              <p className="section-eyebrow">Una soluzione intelligente</p>
              <h3 style={{ fontSize: "1.7rem", fontWeight: 700, color: "#1B2C3E", marginBottom: "0.5rem" }}>
                Innovazione e principio di funzionamento
              </h3>
              <div className="section-divider"></div>
              <p className="prose-text">
                La tecnologia MPW – My Personal Washer rappresenta una rivoluzione nella pulizia
                professionale: un sistema brevettato che unisce aria compressa, detergenza waterless,
                microfibra e aspirazione intelligente in un unico processo integrato.
              </p>
              <p className="prose-text" style={{ marginTop: "1rem" }}>
                Il cuore del sistema è la testa rotante, che distribuisce in pressione il liquido pulente,
                rimuove lo sporco con un'azione microabrasiva controllata e aspira i residui in tempo reale.
                Tutto avviene senza uso d'acqua, garantendo massima efficacia e sicurezza per le superfici.
              </p>
            </div>
            <div className="img-hover" style={{ borderRadius: "12px", overflow: "hidden" }}>
              <img
                src="https://lirp.cdn-website.com/7c20bad1/dms3rep/multi/opt/8+copia1-1017w.png"
                alt="MPW Tecnologia"
                style={{ width: "100%", height: "360px", objectFit: "contain", background: "#f8fcfd", display: "block" }}
              />
            </div>
          </div>

          {/* MPW stats */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
              gap: "1.5rem",
            }}
          >
            {[
              { value: "100%", label: "Riduzione uso acqua" },
              { value: "80%", label: "Riduzione costi smaltimento" },
              { value: "0", label: "Scarichi e sprechi" },
              { value: "100%", label: "Biodegradabile" },
            ].map((stat) => (
              <div
                key={stat.label}
                style={{
                  background: "#f8fcfd",
                  borderRadius: "10px",
                  padding: "2rem 1.5rem",
                  textAlign: "center",
                  borderBottom: "4px solid #17C3CE",
                }}
              >
                <div style={{ fontSize: "2.5rem", fontWeight: 800, color: "#17C3CE", lineHeight: 1, marginBottom: "0.5rem" }}>
                  {stat.value}
                </div>
                <div style={{ fontSize: "0.9rem", color: "#555", fontWeight: 500 }}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ ECOSOSTENIBILITÀ ═══════════════ */}
      <section id="ecosostenibilita" style={{ background: "#f8fcfd", padding: "5rem 2rem" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "3rem" }}>
            <p className="section-eyebrow">Il nostro impegno per il pianeta</p>
            <h2 style={{ fontSize: "2.2rem", fontWeight: 700, color: "#1B2C3E", marginBottom: "0.5rem" }}>
              Ecosostenibilità
            </h2>
            <div className="section-divider-center"></div>
            <p className="prose-text" style={{ maxWidth: "700px", margin: "0 auto" }}>
              In WashSofà, l'ecosostenibilità non è solo una parola: è la base del nostro modello di business.
              Ogni scelta è orientata a ridurre l'impatto ambientale garantendo al contempo la massima efficacia
              dei trattamenti.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "1.5rem",
              marginBottom: "3.5rem",
            }}
          >
            {ECO_POINTS.map((p) => (
              <div
                key={p.title}
                style={{
                  background: "white",
                  borderRadius: "12px",
                  padding: "2rem",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-4px)";
                  e.currentTarget.style.boxShadow = "0 8px 24px rgba(23,195,206,0.15)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.06)";
                }}
              >
                <div
                  style={{
                    width: "52px",
                    height: "52px",
                    background: "rgba(23,195,206,0.1)",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "1.2rem",
                  }}
                >
                  <p.icon size={24} color="#17C3CE" />
                </div>
                <h3 style={{ fontSize: "1rem", fontWeight: 700, color: "#1B2C3E", marginBottom: "0.6rem" }}>
                  {p.title}
                </h3>
                <p style={{ color: "#666", fontSize: "0.92rem", lineHeight: 1.7 }}>{p.desc}</p>
              </div>
            ))}
          </div>

          {/* Impact banner */}
          <div
            style={{
              background: "linear-gradient(135deg, #17C3CE 0%, #0EA5B5 100%)",
              borderRadius: "16px",
              padding: "3rem 2rem",
              color: "white",
              textAlign: "center",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                gap: "2rem",
              }}
            >
              {[
                { num: "100%", lbl: "acqua risparmiata" },
                { num: "80%", lbl: "scarti in meno" },
                { num: "0", lbl: "scarichi nell'ambiente" },
                { num: "100%", lbl: "prodotti sicuri" },
              ].map((s) => (
                <div key={s.lbl}>
                  <div style={{ fontSize: "2.8rem", fontWeight: 800, lineHeight: 1 }}>{s.num}</div>
                  <div style={{ fontSize: "0.95rem", opacity: 0.9, marginTop: "0.4rem" }}>{s.lbl}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ CONTATTI ═══════════════ */}
      <section id="contatti" style={{ padding: "5rem 2rem", background: "#fff" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "3.5rem" }}>
            <p className="section-eyebrow">Siamo a tua disposizione</p>
            <h2 style={{ fontSize: "2.2rem", fontWeight: 700, color: "#1B2C3E", marginBottom: "0.5rem" }}>
              Contattaci
            </h2>
            <div className="section-divider-center"></div>
            <p className="prose-text" style={{ maxWidth: "650px", margin: "0 auto" }}>
              Hai bisogno di un intervento a domicilio a Rieti e provincia? Contattaci per ricevere un preventivo gratuito o per
              fissare subito un appuntamento.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1.6fr",
              gap: "4rem",
              alignItems: "flex-start",
            }}
            className="contact-grid"
          >
            {/* Info */}
            <div>
              <h3 style={{ fontSize: "1.4rem", fontWeight: 700, color: "#1B2C3E", marginBottom: "1.5rem" }}>
                Sede e Recapiti
              </h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "1rem", marginBottom: "2rem" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: "0.8rem" }}>
                  <MapPin size={20} color="#17C3CE" style={{ flexShrink: 0, marginTop: "2px" }} />
                  <span style={{ color: "#444", fontSize: "0.95rem", lineHeight: 1.5 }}>
                    Via Curio Dentato 9<br />(area commerciale Aliante)<br />02100 Rieti (RI)
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "0.8rem" }}>
                  <Phone size={20} color="#17C3CE" style={{ flexShrink: 0 }} />
                  <a href="tel:+393463785564" style={{ color: "#444", fontSize: "0.95rem", textDecoration: "none" }}>
                    +39 3463785564
                  </a>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "0.8rem" }}>
                  <Mail size={20} color="#17C3CE" style={{ flexShrink: 0 }} />
                  <a href="mailto:washsofa1@gmail.com" style={{ color: "#444", fontSize: "0.95rem", textDecoration: "none" }}>
                    washsofa1@gmail.com
                  </a>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "0.8rem" }}>
                  <Clock size={20} color="#17C3CE" style={{ flexShrink: 0 }} />
                  <span style={{ color: "#444", fontSize: "0.95rem" }}>Aperto su Appuntamento</span>
                </div>
              </div>

              <div style={{ display: "flex", gap: "0.8rem" }}>
                <a
                  href="https://www.facebook.com/profile.php?id=61581011145754"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    width: "40px", height: "40px",
                    background: "#f0f9fa",
                    borderRadius: "50%",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "background 0.2s ease",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#17C3CE")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "#f0f9fa")}
                >
                  <Facebook size={18} color="#17C3CE"
                    onMouseEnter={(e) => (e.currentTarget.style.color = "white")} />
                </a>
                <a
                  href="https://www.instagram.com/divano__pulito/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    width: "40px", height: "40px",
                    background: "#f0f9fa",
                    borderRadius: "50%",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "background 0.2s ease",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#17C3CE")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "#f0f9fa")}
                >
                  <Instagram size={18} color="#17C3CE" />
                </a>
              </div>
            </div>

            {/* Form */}
            <div
              style={{
                background: "#f8fcfd",
                borderRadius: "12px",
                padding: "2.5rem",
              }}
            >
              <h3 style={{ fontSize: "1.3rem", fontWeight: 700, color: "#1B2C3E", marginBottom: "1.5rem" }}>
                Richiedi un preventivo
              </h3>

              {submitted ? (
                <div
                  style={{
                    background: "rgba(23,195,206,0.1)",
                    border: "1px solid #17C3CE",
                    borderRadius: "8px",
                    padding: "1.5rem",
                    color: "#0EA5B5",
                    fontWeight: 500,
                    textAlign: "center",
                  }}
                >
                  Grazie per averci contattato.
                  <br />
                  Ti risponderemo il più presto possibile.
                </div>
              ) : (
                <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                  {formError && (
                    <div style={{ background: "#fff3f3", border: "1px solid #ffaaaa", borderRadius: "6px", padding: "0.8rem 1rem", color: "#cc3333", fontSize: "0.9rem" }}>
                      Compila tutti i campi obbligatori.
                    </div>
                  )}
                  {sendError && (
                    <div style={{ background: "#fff3f3", border: "1px solid #ffaaaa", borderRadius: "6px", padding: "0.8rem 1rem", color: "#cc3333", fontSize: "0.9rem" }}>
                      Si è verificato un errore. Riprova più tardi o contattaci direttamente al +39 3463785564.
                    </div>
                  )}

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }} className="form-two-col">
                    <div>
                      <label style={{ fontSize: "0.82rem", fontWeight: 600, color: "#333", display: "block", marginBottom: "0.3rem" }}>Nome *</label>
                      <input type="text" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })}
                        style={{ width: "100%", padding: "0.7rem 1rem", border: "1px solid #ddd", borderRadius: "6px", fontSize: "0.93rem", outline: "none", background: "white" }}
                        onFocus={(e) => (e.currentTarget.style.borderColor = "#17C3CE")}
                        onBlur={(e) => (e.currentTarget.style.borderColor = "#ddd")}
                        required />
                    </div>
                    <div>
                      <label style={{ fontSize: "0.82rem", fontWeight: 600, color: "#333", display: "block", marginBottom: "0.3rem" }}>E-mail *</label>
                      <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                        style={{ width: "100%", padding: "0.7rem 1rem", border: "1px solid #ddd", borderRadius: "6px", fontSize: "0.93rem", outline: "none", background: "white" }}
                        onFocus={(e) => (e.currentTarget.style.borderColor = "#17C3CE")}
                        onBlur={(e) => (e.currentTarget.style.borderColor = "#ddd")}
                        required />
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }} className="form-two-col">
                    <div>
                      <label style={{ fontSize: "0.82rem", fontWeight: 600, color: "#333", display: "block", marginBottom: "0.3rem" }}>Telefono *</label>
                      <input type="tel" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                        style={{ width: "100%", padding: "0.7rem 1rem", border: "1px solid #ddd", borderRadius: "6px", fontSize: "0.93rem", outline: "none", background: "white" }}
                        onFocus={(e) => (e.currentTarget.style.borderColor = "#17C3CE")}
                        onBlur={(e) => (e.currentTarget.style.borderColor = "#ddd")}
                        required />
                    </div>
                    <div>
                      <label style={{ fontSize: "0.82rem", fontWeight: 600, color: "#333", display: "block", marginBottom: "0.3rem" }}>Prodotto d'interesse</label>
                      <select value={form.prodotto} onChange={(e) => setForm({ ...form, prodotto: e.target.value })}
                        style={{ width: "100%", padding: "0.7rem 1rem", border: "1px solid #ddd", borderRadius: "6px", fontSize: "0.93rem", outline: "none", background: "white", cursor: "pointer" }}
                        onFocus={(e) => (e.currentTarget.style.borderColor = "#17C3CE")}
                        onBlur={(e) => (e.currentTarget.style.borderColor = "#ddd")}>
                        <option value="">Seleziona...</option>
                        <option value="Divano">Divano</option>
                        <option value="Tendaggi">Tendaggi</option>
                        <option value="Materassi">Materassi</option>
                        <option value="Tappeti">Tappeti</option>
                        <option value="Pelli">Pelli e Alcantara</option>
                        <option value="Accessori">Accessori Animali</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label style={{ fontSize: "0.82rem", fontWeight: 600, color: "#333", display: "block", marginBottom: "0.3rem" }}>Messaggio</label>
                    <textarea value={form.messaggio} onChange={(e) => setForm({ ...form, messaggio: e.target.value })}
                      rows={4}
                      style={{ width: "100%", padding: "0.7rem 1rem", border: "1px solid #ddd", borderRadius: "6px", fontSize: "0.93rem", outline: "none", background: "white", resize: "vertical" }}
                      onFocus={(e) => (e.currentTarget.style.borderColor = "#17C3CE")}
                      onBlur={(e) => (e.currentTarget.style.borderColor = "#ddd")} />
                  </div>

                  <div style={{ display: "flex", alignItems: "flex-start", gap: "0.6rem" }}>
                    <input type="checkbox" id="privacy" checked={form.privacy} onChange={(e) => setForm({ ...form, privacy: e.target.checked })}
                      style={{ marginTop: "3px", accentColor: "#17C3CE" }} />
                    <label htmlFor="privacy" style={{ fontSize: "0.83rem", color: "#555" }}>
                      Ho letto l'informativa e autorizzo il trattamento dei miei dati personali per le finalità ivi indicate.
                    </label>
                  </div>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: "0.6rem" }}>
                    <input type="checkbox" id="promozioni" checked={form.promozioni} onChange={(e) => setForm({ ...form, promozioni: e.target.checked })}
                      style={{ marginTop: "3px", accentColor: "#17C3CE" }} />
                    <label htmlFor="promozioni" style={{ fontSize: "0.83rem", color: "#555" }}>
                      Ricevi offerte speciali e promozioni dedicate
                    </label>
                  </div>

                  <button type="submit"
                    disabled={sending}
                    style={{ background: sending ? "#8adde5" : "#17C3CE", color: "white", border: "none", padding: "0.9rem 2rem", borderRadius: "50px", fontSize: "1rem", fontWeight: 700, cursor: sending ? "not-allowed" : "pointer", marginTop: "0.3rem", transition: "background 0.2s ease" }}
                    onMouseEnter={(e) => { if (!sending) e.currentTarget.style.background = "#12AABA"; }}
                    onMouseLeave={(e) => { if (!sending) e.currentTarget.style.background = "#17C3CE"; }}>
                    {sending ? "Invio in corso..." : "Richiedi preventivo"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ FAQ ═══════════════ */}
      <section style={{ background: "#f8fcfd", padding: "5rem 2rem" }}>
        <div style={{ maxWidth: "800px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "3rem" }}>
            <h2 style={{ fontSize: "2rem", fontWeight: 700, color: "#1B2C3E" }}>Domande Generali</h2>
            <div className="section-divider-center"></div>
          </div>
          {FAQ.map((faq, i) => (
            <div
              key={i}
              style={{
                background: "white",
                borderRadius: "8px",
                marginBottom: "0.8rem",
                overflow: "hidden",
                boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
              }}
            >
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                style={{
                  width: "100%", padding: "1.2rem 1.5rem",
                  background: "none", border: "none",
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  cursor: "pointer", textAlign: "left", gap: "1rem",
                }}
              >
                <span style={{ fontWeight: 600, color: "#1B2C3E", fontSize: "0.95rem" }}>{faq.q}</span>
                {openFaq === i
                  ? <ChevronUp size={18} color="#17C3CE" style={{ flexShrink: 0 }} />
                  : <ChevronDown size={18} color="#888" style={{ flexShrink: 0 }} />}
              </button>
              {openFaq === i && (
                <div style={{ padding: "0 1.5rem 1.2rem 1.5rem", color: "#555", fontSize: "0.93rem", lineHeight: 1.7, borderTop: "1px solid #f0f0f0" }}>
                  <div style={{ paddingTop: "0.8rem" }}>{faq.a}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ═══════════════ CTA FINALE ═══════════════ */}
      <div className="cta-section">
        <h2 style={{ fontSize: "1.8rem", fontWeight: 700, marginBottom: "0.5rem" }}>
          Prova la differenza del nostro sistema senz'acqua
        </h2>
        <p style={{ opacity: 0.9, fontSize: "1rem" }}>Pulizia profonda, zero sprechi, risultati immediati.</p>
        <a href="tel:+393463785564" className="btn-cta-white">
          <Phone size={18} />
          Chiama ora &nbsp;&nbsp;+39 3463785564
        </a>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .two-col-grid,
          .service-grid,
          .contact-grid {
            grid-template-columns: 1fr !important;
            gap: 2rem !important;
          }
          .service-grid > div { order: unset !important; }
          .form-two-col { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
